

import { Hero } from './data/heroes';
import { Card, allCards } from './data/cards';
import { Pairing } from './pairing';
import { MAX_GOLD, BASE_GOLD_PER_TURN, TAVERN_UPGRADE_COSTS } from './data/gameConfig';

// A runtime instance of a card, which is guaranteed to have a unique ID.
type CardInstance = Card;

// Board state for tracking player readiness
export type BoardState = 'editing' | 'ready' | 'locked';

// Combat history record for tracking previous battles
export interface CombatHistoryRecord {
  round: number;
  opponentId: string;
  opponentName: string;
  result: 'win' | 'loss' | 'draw';
  damageTaken: number;
  damageDealt: number;
}

// We define an interface for a single player's state for type safety.
export interface PlayerState {
  id: string;
  name: string;
  isHuman: boolean;
  health: number;
  gold: number;
  tavernTier: number;
  turn: number;
  board: (CardInstance | null)[];
  hand: CardInstance[];
  hero: Hero | null;
  alive: boolean;
  heroPowerUsedThisTurn: boolean;
  combatHistory: CombatHistoryRecord[];
  refreshCountThisTurn: number; // Tracks how many times shop was refreshed this turn
  boardState: BoardState; // Tracks if player has finalized their board
  shopFrozen: boolean; // When true, shop cards persist to next turn
  frozenShopCards: Card[]; // Stored frozen shop cards
}

// Triple reward tracking
export interface TripleReward {
  playerId: string;
  baseTier: number;
}

// The main GameState interface that holds all players and global data.
export interface GameState {
  players: PlayerState[];
  humanIndex: number;
  roundNumber: number;
  pairings: Pairing[];
  pendingTripleRewards: TripleReward[];
}

// The main gameState object that holds all game data.
export const gameState: GameState = {
  players: [],
  humanIndex: 0,
  roundNumber: 0,
  pairings: [],
  pendingTripleRewards: [],
};

/**
 * Initializes a new 8-player lobby.
 * @param humanHero - The hero chosen by the human player.
 * @param starterCardIds - An array of card IDs chosen by the player for their hand.
 */
export function initNewLobby(humanHero: Hero, starterCardIds: string[]): void {
  const players: PlayerState[] = [];
  const humanIndex = 0;

  // Create human player's starting hand from selected card IDs
  const humanHand: CardInstance[] = starterCardIds.map(id => {
    const cardTemplate = allCards.find(c => c.id === id);
    if (!cardTemplate) throw new Error(`Card with id ${id} not found`);
    return { ...cardTemplate, instanceId: crypto.randomUUID() };
  });

  // Add the human player to the lobby
  players.push({
    id: `P${humanIndex}`,
    name: 'You',
    isHuman: true,
    health: 30,
    gold: 3,
    tavernTier: 1,
    turn: 1,
    board: Array(7).fill(null),
    hand: humanHand,
    hero: humanHero,
    alive: true,
    heroPowerUsedThisTurn: false,
    combatHistory: [],
    refreshCountThisTurn: 0,
    boardState: 'editing',
    shopFrozen: false,
    frozenShopCards: [],
  });

  // Create and add 7 AI bot players
  for (let i = 1; i < 8; i++) {
    players.push({
      id: `P${i}`,
      name: `Bot ${i}`,
      isHuman: false,
      health: 30,
      gold: 3,
      tavernTier: 1,
      turn: 1,
      board: Array(7).fill(null),
      hand: [],
      hero: null, // Placeholder for now
      alive: true,
      heroPowerUsedThisTurn: false,
      combatHistory: [],
      refreshCountThisTurn: 0,
      boardState: 'editing',
      shopFrozen: false,
      frozenShopCards: [],
    });
  }

  // Update the global game state object
  gameState.players = players;
  gameState.humanIndex = humanIndex;
  gameState.roundNumber = 1;
  gameState.pairings = [];

  console.log('New lobby initialized. Game state:', JSON.parse(JSON.stringify(gameState)));
}

/**
 * A helper function to get the human player's state.
 * @returns The current human player's state object.
 * @throws An error if the human player is not found.
 */
export function getHumanPlayer(): PlayerState {
  const player = gameState.players[gameState.humanIndex];
  if (!player) {
    throw new Error("Human player not found. Game state may be uninitialized.");
  }
  return player;
}

/**
 * Gets all opponent players who are still alive.
 * @returns An array of alive opponent PlayerState objects.
 */
export function getAliveOpponents(): PlayerState[] {
  return gameState.players.filter(p => !p.isHuman && p.alive);
}

/**
 * Finds a player by their unique ID.
 * @param id The ID of the player to find.
 * @returns The PlayerState object or undefined if not found.
 */
export function getPlayerById(id: string): PlayerState | undefined {
  return gameState.players.find(p => p.id === id);
}


/**
 * A helper function to get the currently selected hero for the human player.
 * Returns null (rather than throwing) when the singleton is uninitialized —
 * hero-power hooks run inside tavern actions, and the v2 store can be
 * rehydrated from persistence before initNewLobby has seeded players[].
 * @returns The current hero object or null if none is selected.
 */
export function getCurrentHero(): Hero | null {
  return gameState.players[gameState.humanIndex]?.hero ?? null;
}

/**
 * Checks if the human player's active hero power can be used.
 * @returns {boolean} True if the hero power is usable, false otherwise.
 */
export function canUseHeroPower(): boolean {
  const player = getHumanPlayer();
  const { hero, gold, heroPowerUsedThisTurn } = player;
  if (!hero || !hero.isActive || heroPowerUsedThisTurn) {
    return false;
  }
  const cost = hero.cost || 0;
  return gold >= cost;
}

/**
 * Marks the hero power as used for the turn and deducts its gold cost for the human player.
 */
export function markHeroPowerUsed(): void {
  const player = getHumanPlayer();
  const { hero } = player;
  if (!hero || !hero.isActive) return;

  const cost = hero.cost || 0;
  if (player.gold >= cost) {
    player.gold -= cost;
    player.heroPowerUsedThisTurn = true;
  }
}

/**
 * Gets the cost for the human player to upgrade to their next tavern tier.
 * @returns The gold cost, or Infinity if at max tier.
 */
export function getTavernUpgradeCost(): number {
  const player = getHumanPlayer();
  if (player.tavernTier >= 6) {
    return Infinity;
  }
  // TAVERN_UPGRADE_COSTS is indexed by the tier to upgrade TO.
  return TAVERN_UPGRADE_COSTS[player.tavernTier + 1];
}

/**
 * Advances the game to the next round, updating state for all players.
 */
export function advanceToNextRound(): void {
  gameState.roundNumber++;
  for (const player of gameState.players) {
    if (player.alive) {
      player.turn++;
      // Gold is the round number + a base, capped at MAX_GOLD.
      player.gold = Math.min(MAX_GOLD, gameState.roundNumber + BASE_GOLD_PER_TURN);
      player.heroPowerUsedThisTurn = false;
      player.refreshCountThisTurn = 0;
      player.boardState = 'editing'; // Reset board state for new round

      // Reset combat-only buffs and increment turn counters on all board units
      for (const unit of player.board) {
          if (unit) {
              unit.attackBonusForCombat = 0;
              unit.healthBonusForCombat = 0;
              // Increment turn counter for abilities like "every second turn"
              unit.turnsSurvived = (unit.turnsSurvived || 0) + 1;
              // Reset per-combat tracking
              unit.timesHitThisCombat = 0;
          }
      }
    }
  }
}

/**
 * Records a combat result in both players' combat history.
 * @param playerAId - The ID of the first player
 * @param playerBId - The ID of the second player
 * @param winner - 'player' (A wins), 'enemy' (B wins), or 'draw'
 * @param damage - The damage dealt
 */
export function recordCombatResult(
  playerAId: string,
  playerBId: string,
  winner: 'player' | 'enemy' | 'draw',
  damage: number
): void {
  const playerA = getPlayerById(playerAId);
  const playerB = getPlayerById(playerBId);

  if (!playerA || !playerB) return;

  // Record for player A
  const resultForA: 'win' | 'loss' | 'draw' =
    winner === 'draw' ? 'draw' : (winner === 'player' ? 'win' : 'loss');

  playerA.combatHistory.push({
    round: gameState.roundNumber,
    opponentId: playerB.id,
    opponentName: playerB.name,
    result: resultForA,
    damageTaken: winner === 'enemy' ? damage : 0,
    damageDealt: winner === 'player' ? damage : 0,
  });

  // Record for player B
  const resultForB: 'win' | 'loss' | 'draw' =
    winner === 'draw' ? 'draw' : (winner === 'player' ? 'loss' : 'win');

  playerB.combatHistory.push({
    round: gameState.roundNumber,
    opponentId: playerA.id,
    opponentName: playerA.name,
    result: resultForB,
    damageTaken: winner === 'player' ? damage : 0,
    damageDealt: winner === 'enemy' ? damage : 0,
  });
}

/**
 * Gets the most recent combat result for a player against a specific opponent.
 * @param playerId - The ID of the player
 * @param opponentId - The ID of the opponent
 * @returns The most recent combat history record or undefined if no history exists
 */
export function getLastResultAgainst(
  playerId: string,
  opponentId: string
): CombatHistoryRecord | undefined {
  const player = getPlayerById(playerId);
  if (!player) return undefined;

  // Find the most recent combat against this opponent
  for (let i = player.combatHistory.length - 1; i >= 0; i--) {
    if (player.combatHistory[i].opponentId === opponentId) {
      return player.combatHistory[i];
    }
  }

  return undefined;
}

/**
 * Gets the current opponent for a specific player based on the current pairings.
 * @param playerId - The ID of the player
 * @returns The opponent's PlayerState or undefined if no opponent is found
 */
export function getCurrentOpponent(playerId: string): PlayerState | undefined {
  const pairing = gameState.pairings.find(
    p => p.playerAId === playerId || p.playerBId === playerId
  );

  if (!pairing) return undefined;

  const opponentId = pairing.playerAId === playerId ? pairing.playerBId : pairing.playerAId;
  return getPlayerById(opponentId);
}

/**
 * Checks if all opponents of a specific player are ready.
 * @param playerId - The ID of the player to check opponents for
 * @returns True if all alive opponents have boardState 'ready' or 'locked'
 */
export function areAllOpponentsReady(playerId: string): boolean {
  const opponents = gameState.players.filter(
    p => p.id !== playerId && p.alive
  );

  if (opponents.length === 0) return true;

  return opponents.every(
    opponent => opponent.boardState === 'ready' || opponent.boardState === 'locked'
  );
}

/**
 * Gets the count of ready opponents for a specific player.
 * @param playerId - The ID of the player to check opponents for
 * @returns An object with ready count and total count
 */
export function getOpponentReadyCount(playerId: string): { ready: number; total: number } {
  const opponents = gameState.players.filter(
    p => p.id !== playerId && p.alive
  );

  const readyCount = opponents.filter(
    opponent => opponent.boardState === 'ready' || opponent.boardState === 'locked'
  ).length;

  return {
    ready: readyCount,
    total: opponents.length
  };
}

/**
 * Sets a player's board state.
 * @param playerId - The ID of the player
 * @param state - The new board state
 */
export function setPlayerBoardState(playerId: string, state: BoardState): void {
  const player = getPlayerById(playerId);
  if (player) {
    player.boardState = state;
  }
}

// --- Game State Persistence ---
const GAME_STATE_KEY = 'cholos_vs_emos_saved_game';
// Bump when the persisted state shape changes (new fields on Card, PlayerState,
// tribes, etc.) so older saves are invalidated rather than crashing the
// scene that consumes them.
const SAVE_VERSION = 1;

/**
 * Saves the current game state to localStorage for crash recovery.
 */
export function saveGameState(): void {
  try {
    const stateToSave = {
      __version: SAVE_VERSION,
      players: gameState.players,
      humanIndex: gameState.humanIndex,
      roundNumber: gameState.roundNumber,
      pairings: gameState.pairings,
      pendingTripleRewards: gameState.pendingTripleRewards,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(GAME_STATE_KEY, JSON.stringify(stateToSave));
    console.log('[SAVE] Game state saved successfully');
  } catch (error) {
    console.error('[SAVE] Failed to save game state:', error);
  }
}

/**
 * Loads the saved game state from localStorage.
 * @returns True if state was loaded successfully, false otherwise.
 *          Stale or malformed saves are discarded so the next launch is clean.
 */
export function loadGameState(): boolean {
  try {
    const savedData = localStorage.getItem(GAME_STATE_KEY);
    if (!savedData) {
      return false;
    }

    const loadedState = JSON.parse(savedData);

    if (loadedState?.__version !== SAVE_VERSION) {
      console.warn('[LOAD] Saved game version mismatch (got', loadedState?.__version, 'expected', SAVE_VERSION, '). Discarding.');
      clearSavedGameState();
      return false;
    }

    if (!Array.isArray(loadedState.players) || typeof loadedState.humanIndex !== 'number' || typeof loadedState.roundNumber !== 'number') {
      console.warn('[LOAD] Saved game shape invalid. Discarding.');
      clearSavedGameState();
      return false;
    }

    // Restore the game state
    gameState.players = loadedState.players;
    gameState.humanIndex = loadedState.humanIndex;
    gameState.roundNumber = loadedState.roundNumber;
    gameState.pairings = loadedState.pairings || [];
    gameState.pendingTripleRewards = loadedState.pendingTripleRewards || [];

    console.log('[LOAD] Game state loaded successfully from:', loadedState.savedAt);
    return true;
  } catch (error) {
    console.error('[LOAD] Failed to load game state:', error);
    clearSavedGameState();
    return false;
  }
}

/**
 * Clears the saved game state from localStorage.
 */
export function clearSavedGameState(): void {
  try {
    localStorage.removeItem(GAME_STATE_KEY);
    console.log('[SAVE] Saved game state cleared');
  } catch (error) {
    console.error('[SAVE] Failed to clear saved game state:', error);
  }
}

/**
 * Checks if there is a saved game state in localStorage.
 * @returns True if a saved game exists, false otherwise.
 */
export function hasSavedGameState(): boolean {
  return localStorage.getItem(GAME_STATE_KEY) !== null;
}

/**
 * Gets metadata about the saved game without loading it.
 * @returns Saved game info or null if no saved game exists.
 */
export function getSavedGameInfo(): { roundNumber: number; savedAt: string; heroId: string | null } | null {
  try {
    const savedData = localStorage.getItem(GAME_STATE_KEY);
    if (!savedData) {
      return null;
    }

    const loadedState = JSON.parse(savedData);
    const humanPlayer = loadedState.players[loadedState.humanIndex];

    return {
      roundNumber: loadedState.roundNumber,
      savedAt: loadedState.savedAt,
      heroId: humanPlayer?.hero?.id || null,
    };
  } catch (error) {
    return null;
  }
}

// --- Debugging ---
// Expose key game state and functions to the window object for easy console access.
if (typeof window !== 'undefined') {
  (window as any).gameState = gameState;
  (window as any).getHumanPlayer = getHumanPlayer;
  (window as any).getPlayerById = getPlayerById;
  (window as any).getCurrentHero = getCurrentHero;
  (window as any).areAllOpponentsReady = areAllOpponentsReady;
  (window as any).getOpponentReadyCount = getOpponentReadyCount;
  (window as any).saveGameState = saveGameState;
  (window as any).loadGameState = loadGameState;
  (window as any).clearSavedGameState = clearSavedGameState;
  (window as any).hasSavedGameState = hasSavedGameState;
}
